import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import api from "../../api";
import HtmlWithLatex from "./components/HtmlWithLatex";
import McqOptions from "./components/McqOptions";
import AnswerOptionRichEditor from "./components/AnswerOptionRichEditor";
import JobDeleteGeneratedDialog, { ENTITY_LABELS } from "./components/JobDeleteGeneratedDialog";
import SaveToBankDestinationDialog, {
  CREATE_TARGET_LABELS,
  validateDestinationForm,
} from "./components/SaveToBankDestinationDialog";
import ReviewFilterBar from "./components/ReviewFilterBar";

const collectImageUrls = (question) => {
  const imageUrls = Array.isArray(question?.image_urls)
    ? question.image_urls.filter(Boolean)
    : [];
  return imageUrls.length > 0 ? imageUrls : question?.image_url ? [question.image_url] : [];
};

const getTableLayout = (question) =>
  question?.table_layout ||
  question?.answer_data?.table_layout ||
  null;

// border/cellpadding are dropped by the HTML sanitizer, so the grid has to be styled inline.
const TABLE_CELL_STYLE = "border:1px solid #999;padding:6px 10px;text-align:center";

const renderTableLayoutHtml = (layout) => {
  const grid = layout?.grid;
  if (!Array.isArray(grid) || grid.length === 0) return "";

  const rows = grid
    .map((row, r) => {
      if (!Array.isArray(row)) return "";
      const cells = row
        .map((cell, c) => {
          if (!cell || typeof cell !== "object") return `<td style="${TABLE_CELL_STYLE}"></td>`;
          if (Object.prototype.hasOwnProperty.call(cell, "f")) {
            const text = cell.f == null || cell.f === "" ? "" : String(cell.f);
            // Given data in an already-completed row is not a heading; only the top row and a
            // non-numeric first column are.
            const isHeader =
              text !== "" &&
              (r === 0 || (c === 0 && Number.isNaN(Number(text.replace(/[,\s£]/g, "")))));
            const tag = isHeader ? "th" : "td";
            return `<${tag} style="${TABLE_CELL_STYLE}">${text}</${tag}>`;
          }
          if (Object.prototype.hasOwnProperty.call(cell, "b")) {
            return `<td style="${TABLE_CELL_STYLE}">___</td>`;
          }
          return `<td style="${TABLE_CELL_STYLE}"></td>`;
        })
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");

  return `<table style="border-collapse:collapse;margin:8px 0">${rows}</table>`;
};

const stemHasTable = (text) => /<table\b/i.test(String(text || ""));

const displayStemHtml = (question) => {
  const text = question?.question_text || "";
  if (stemHasTable(text)) return text;
  const layout = getTableLayout(question);
  if (!layout) {
    if (question?.original_table_html) {
      return `${text}\n\n${question.original_table_html}`;
    }
    return text;
  }
  return `${text}\n\n${renderTableLayoutHtml(layout)}`;
};

const questionSubject = (question, fallback = "") =>
  question?.subject?.name || question?.subject_name || question?.exam_subject || fallback || "";

const normalizeSubject = (subject) => {
  const normalized = String(subject || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

  if (!normalized) return { key: "", label: "" };
  if (normalized.includes("non verbal") || normalized === "nvr") {
    return { key: "non-verbal-reasoning", label: "Non-Verbal Reasoning" };
  }
  if (normalized.includes("verbal") || normalized === "vr") {
    return { key: "verbal-reasoning", label: "Verbal Reasoning" };
  }
  if (normalized.includes("math")) return { key: "maths", label: "Maths" };
  if (normalized.includes("english")) return { key: "english", label: "English" };

  return {
    key: normalized,
    label: normalized.replace(/\b\w/g, (letter) => letter.toUpperCase()),
  };
};

/** Why a question could not be turned into a fair MCQ. Shown instead of hiding it. */
const SKIP_REASONS = {
  already_mcq: "Already a multiple-choice question — saved as-is if you approve it.",
  no_solution_image: "Drawing question has no rendered solution image, so no image-based option could be built.",
  open_essay: "Open-ended essay — has no single correct answer to build options from.",
  no_answer: "No mark scheme stored, so the correct option cannot be determined.",
  no_convertible_sub_questions: "None of the sub-questions could be converted.",
  correct_option_does_not_match_mark_scheme: "The AI's chosen correct option didn't match the official mark scheme, even after a retry and automatic repair. Regenerate to try again.",
  multiple_correct_options: "The AI returned more than one correct option for a single-answer question and they didn't match the mark scheme as a set.",
  too_few_options: "Fewer than 3 usable options could be built.",
  no_correct_option: "The AI didn't mark any option as correct.",
  unparseable_ai_response: "The AI response couldn't be parsed. Try regenerating.",
  option_image_generation_failed: "Could not generate diagram images for this drawing question's options. Try regenerating, or add a solution image to the question.",
  no_ai_provider_available: "No AI provider is configured/available for conversion.",
};

/** Badge shown when a conversion needed automatic repair rather than a clean AI pass. */
const REPAIR_LABELS = {
  correct_option_replaced: "Correct option replaced with mark scheme (auto-repaired)",
  ai_generated_option_images: "Option images AI-generated (no solution image existed)",
  ai_authored_answer: "Open-ended question — correct answer written by AI, not a mark scheme. Check it.",
  ai_authored_set_placement: "Sorting/Venn question — options are complete placements written by AI. Check the correct one.",
  rendered_option_images: "Options are pictures of the completed table/figure — the correct one is drawn from the mark scheme, the others from wrong fillings.",
};

const statusChip = (entry) => {
  if (entry.status === "converted") return { label: "Converted", color: "success" };
  if (entry.status === "skipped") return { label: "Skipped", color: "warning" };
  return { label: "Failed", color: "error" };
};

/** Landing view when the page is opened from the sidebar with no job in the URL. */
const McqJobPicker = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [savedFilter, setSavedFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [deleteJobTarget, setDeleteJobTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await api.get("/admin/question-bank/mcq-jobs", { params: { per_page: 25 } });
      setJobs(res.data?.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load conversions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleDelete = async (payload) => {
    setDeleting(true);
    try {
      const res = await api.delete(`/admin/question-bank/mcq-job/${deleteJobTarget.id}/generated`, {
        data: payload,
      });
      toast.success(res.data?.message || "Generated questions deleted.");
      setDeleteJobTarget(null);
      await fetchJobs();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete generated questions.");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  const jobStatuses = [...new Set(jobs.map((j) => j.status).filter(Boolean))];
  const jobSubjectMap = new Map();
  jobs.forEach((job) => {
    const subject = normalizeSubject(job.import?.exam_subject);
    if (subject.key) jobSubjectMap.set(subject.key, subject);
  });
  const jobSubjects = [...jobSubjectMap.values()].sort((a, b) => a.label.localeCompare(b.label));

  const isSaved = (j) => (j.imported_question_ids || []).length > 0;

  const visibleJobs = jobs.filter((j) => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (savedFilter === "saved" && !isSaved(j)) return false;
    if (savedFilter === "unsaved" && isSaved(j)) return false;
    if (
      subjectFilter !== "all" &&
      normalizeSubject(j.import?.exam_subject).key !== subjectFilter
    ) return false;
    const term = search.trim().toLowerCase();
    if (!term) return true;
    return [j.import?.source_filename, j.import?.exam_title, j.import?.exam_subject, `#${j.id}`]
      .join(" ")
      .toLowerCase()
      .includes(term);
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
        MCQ Conversions
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Papers converted into multiple-choice questions. Open one to review and save it.
      </Typography>

      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 2, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}
      >
        <TextField
          size="small"
          placeholder="Search by file name, exam title or subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: "1 1 260px" }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All statuses</MenuItem>
            {jobStatuses.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Saved</InputLabel>
          <Select label="Saved" value={savedFilter} onChange={(e) => setSavedFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="saved">Saved to bank</MenuItem>
            <MenuItem value="unsaved">Not saved yet</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Subject</InputLabel>
          <Select
            label="Subject"
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            <MenuItem value="all">All subjects</MenuItem>
            {jobSubjects.map((subject) => (
              <MenuItem key={subject.key} value={subject.key}>{subject.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {visibleJobs.length} of {jobs.length}
        </Typography>
      </Paper>

      {jobs.length === 0 ? (
        <Alert severity="info">
          No conversions yet. Use &ldquo;Convert to MCQ&rdquo; on a paper in Recent Imports or
          Papers (PDF).
        </Alert>
      ) : visibleJobs.length === 0 ? (
        <Alert severity="info">No conversions match the current filters.</Alert>
      ) : (
        <Stack spacing={1}>
          {visibleJobs.map((j) => (
            <Paper
              key={j.id}
              variant="outlined"
              sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
            >
              <Typography sx={{ fontWeight: 600, flex: 1, minWidth: 200 }}>
                {j.import?.source_filename || `Import #${j.import_id}`}
              </Typography>
              {j.import?.exam_subject && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={normalizeSubject(j.import.exam_subject).label}
                />
              )}
              <Chip size="small" label={j.status} />
              {isSaved(j) && <Chip size="small" color="info" variant="outlined" label="saved" />}
              {(j.created_entities || []).map((e) => (
                <Chip
                  key={`${e.entity_type}-${e.entity_id}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${ENTITY_LABELS[e.entity_type] || e.entity_type}: ${e.name}`}
                />
              ))}
              {j.stats?.converted != null && (
                <Chip size="small" color="success" label={`${j.stats.converted} converted`} />
              )}
              <Button
                size="small"
                variant="contained"
                onClick={() => navigate(`/admin/question-bank/mcq-review?job=${j.id}`)}
              >
                Open
              </Button>
              <Tooltip title="Delete generated questions (and anything created from them)">
                <IconButton size="small" color="error" onClick={() => setDeleteJobTarget(j)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          ))}
        </Stack>
      )}

      <JobDeleteGeneratedDialog
        nounSingular="generated MCQ"
        listLabel="MCQ Conversions"
        regenerateHint="Convert to MCQ"
        open={Boolean(deleteJobTarget)}
        job={deleteJobTarget}
        busy={deleting}
        onClose={() => setDeleteJobTarget(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
};

const McqReview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job");

  const [job, setJob] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [approved, setApproved] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenIdx, setRegenIdx] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [saveOpen, setSaveOpen] = useState(false);
  const [categoryId, setCategoryId] = useState("");

  // ── destination (Mock Exam / Past Paper / Worksheet) picked at save time ──
  const [destination, setDestination] = useState("mock_exam");
  const [entityForm, setEntityForm] = useState({ name: "", format_id: "", school_year_id: "", topic_id: "", price: 0 });
  const [lookupFormats, setLookupFormats] = useState([]);
  const [lookupSchoolYears, setLookupSchoolYears] = useState([]);
  const [lookupTopics, setLookupTopics] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);

  /** Key of the question/sub-question stem currently swapped into edit mode ("<index>" or "<index>-<sIdx>"). */
  const [editingTextKey, setEditingTextKey] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [approvalFilter, setApprovalFilter] = useState("all");

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/question-bank/mcq-job/${jobId}`);
      const data = res.data?.data;
      setJob(data);
      setQuestions(data?.generated_questions || []);
      setCategoryId(data?.target_category_id || "");

      // Converted questions are approved by default; skipped/failed ones are opt-in.
      setApproved(
        new Set(
          (data?.generated_questions || [])
            .map((entry, i) => (entry.status === "converted" ? i : null))
            .filter((i) => i !== null)
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load conversion job.");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const toggleApproved = (index) => {
    setApproved((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const updateQuestion = (index, updater) => {
    setQuestions((prev) => {
      const next = [...prev];
      const entry = { ...next[index] };
      entry.question = updater({ ...entry.question });
      next[index] = entry;
      return next;
    });
  };

  const updateSubQuestion = (index, sIdx, updater) => {
    updateQuestion(index, (draft) => {
      const subs = [...(draft.sub_questions || [])];
      subs[sIdx] = updater({ ...subs[sIdx] });
      return { ...draft, sub_questions: subs };
    });
  };

  const moveSubQuestion = (index, sIdx, direction) => {
    setQuestions((prev) => {
      const next = [...prev];
      const entry = { ...next[index] };
      const q = { ...entry.question };
      const subs = [...(q.sub_questions || [])];
      const target = sIdx + direction;
      if (target < 0 || target >= subs.length) return prev;
      [subs[sIdx], subs[target]] = [subs[target], subs[sIdx]];
      q.sub_questions = subs.map((sq, i) => ({ ...sq, sort_order: i }));
      entry.question = q;
      next[index] = entry;
      persistDraft(next);
      return next;
    });
  };

  const persistDraft = async (nextQuestions) => {
    try {
      await api.post(`/admin/question-bank/mcq-job/${jobId}/update-questions`, {
        generated_questions: nextQuestions,
      });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save your edits.");
    }
  };

  const handleRegenerate = async (index) => {
    setRegenIdx(index);
    try {
      const res = await api.post(`/admin/question-bank/mcq-job/${jobId}/regenerate/${index}`);
      const entry = res.data?.data?.question;
      setQuestions((prev) => {
        const next = [...prev];
        next[index] = entry;
        return next;
      });
      if (entry?.status === "converted") {
        setApproved((prev) => new Set(prev).add(index));
        toast.success("Question regenerated.");
      } else {
        toast.warning(`Could not convert: ${entry?.reason || "unknown reason"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to regenerate.");
    } finally {
      setRegenIdx(null);
    }
  };

  const handleDeleteGenerated = async (payload) => {
    setDeleting(true);
    try {
      const res = await api.delete(`/admin/question-bank/mcq-job/${jobId}/generated`, {
        data: payload,
      });
      toast.success(res.data?.message || "Generated questions deleted.");
      setDeleteConfirmOpen(false);
      if (res.data?.data?.job_deleted) {
        navigate("/admin/question-bank/mcq-review");
        return;
      }
      await fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete generated questions.");
    } finally {
      setDeleting(false);
    }
  };

  const fetchEntityLookups = useCallback(async (target) => {
    setLookupsLoading(true);
    try {
      if (target === "mock_exam" || target === "paper") {
        const [f, y] = await Promise.all([
          api.get("common/data?param=Formats"),
          api.get("common/data?param=SchoolYears"),
        ]);
        setLookupFormats(f.data?.data || f.data || []);
        setLookupSchoolYears(y.data?.data || y.data || []);
      } else if (target === "worksheet") {
        const subjectId = job?.import?.subject_id;
        if (subjectId) {
          const t = await api.get("/admin/question-bank/topics-by-subject", { params: { subject_id: subjectId } });
          setLookupTopics(t.data?.data || []);
        } else {
          setLookupTopics([]);
        }
      }
    } catch {
      toast.error("Failed to load options");
    } finally {
      setLookupsLoading(false);
    }
  }, [job]);

  const openSaveDialog = () => {
    const defName = job?.import?.exam_title || job?.import?.source_filename || `MCQ Conversion #${job?.id}`;
    setDestination("mock_exam");
    setEntityForm({ name: defName, format_id: "", school_year_id: "", topic_id: "", price: 0 });
    setSaveOpen(true);
    fetchEntityLookups("mock_exam");
  };

  const changeDestination = (target) => {
    setDestination(target);
    fetchEntityLookups(target);
  };

  const handleSave = async () => {
    if (!validateDestinationForm(destination, entityForm)) {
      return;
    }

    setSaving(true);
    try {
      await persistDraft(questions);

      const sourceTypeForConfirm = destination === "mock_exam"
        ? "mock_exam"
        : destination === "paper"
          ? "past-paper"
          : "ai_generated";

      await api.post(`/admin/question-bank/mcq-job/${jobId}/confirm`, {
        approved_indices: [...approved],
        questions,
        target_source_type: sourceTypeForConfirm,
        target_category_id: categoryId || null,
      });

      const payload = { target: destination, name: entityForm.name.trim(), category_id: categoryId || undefined };
      if (destination === "mock_exam" || destination === "paper") {
        payload.format_id = entityForm.format_id;
        payload.school_year_id = entityForm.school_year_id;
        payload.price = entityForm.price || 0;
      }
      if (destination === "worksheet") payload.topic_id = entityForm.topic_id;

      const res = await api.post(`/admin/question-bank/mcq-job/${jobId}/create-entity`, payload);
      const d = res.data?.data;
      toast.success(res.data?.message || `${CREATE_TARGET_LABELS[destination]} created successfully`);
      setSaveOpen(false);
      navigate(d?.edit_path || `/admin/question-bank?import_id=${job?.import_id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save to the question bank.");
    } finally {
      setSaving(false);
    }
  };

  const stats = job?.stats || {};

  const availableTypes = [...new Set(questions.map((e) => e.question?.type).filter(Boolean))];
  const availableSubjectMap = new Map();
  questions.forEach((entry) => {
    const subject = normalizeSubject(
      questionSubject(entry.question, job?.import?.exam_subject)
    );
    if (subject.key) availableSubjectMap.set(subject.key, subject);
  });
  const availableSubjects = [...availableSubjectMap.values()]
    .sort((a, b) => a.label.localeCompare(b.label));

  const plain = (html) => String(html || "").replace(/<[^>]*>/g, " ").toLowerCase();

  const visible = questions
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry, index }) => {
      if (statusFilter !== "all" && entry.status !== statusFilter) return false;
      if (typeFilter !== "all" && entry.question?.type !== typeFilter) return false;
      if (
        subjectFilter !== "all" &&
        normalizeSubject(
          questionSubject(entry.question, job?.import?.exam_subject)
        ).key !== subjectFilter
      ) return false;
      if (approvalFilter === "approved" && !approved.has(index)) return false;
      if (approvalFilter === "unapproved" && approved.has(index)) return false;
      const term = search.trim().toLowerCase();
      if (!term) return true;
      const q = entry.question || {};
      const haystack = [
        plain(q.question_text),
        plain(q.mark_scheme_answer),
        ...(q.options || []).map((o) => plain(o.text || o.option_text)),
        ...(q.sub_questions || []).flatMap((sq) => [
          plain(sq.question_text),
          ...(sq.options || []).map((o) => plain(o.text || o.option_text)),
        ]),
      ].join(" ");
      return haystack.includes(term);
    });

  const setApprovalForVisible = (shouldApprove) => {
    setApproved((prev) => {
      const next = new Set(prev);
      visible.forEach(({ index }) => (shouldApprove ? next.add(index) : next.delete(index)));
      return next;
    });
  };

  /** Options editor for any question/sub-question object, writing back through the given updater. */
  const renderOptionsEditorFor = (q, applyUpdater) => {
    const isMultiSelect = q.type === "multiple-select";

    const options = (q.options || []).map((o) => ({
      text: o.text || o.option_text || "",
      image_url: o.image_url || null,
    }));
    const correctIndices = (q.options || [])
      .map((o, i) => (o.is_correct ? i : null))
      .filter((i) => i !== null);

    const writeBack = (newOptions, newCorrect) =>
      applyUpdater((draft) => ({
        ...draft,
        options: newOptions.map((o, i) => ({
          ...(draft.options?.[i] || {}),
          text: o.text,
          option_text: o.text,
          image_url: o.image_url ?? null,
          is_correct: newCorrect.includes(i),
        })),
        answer_data: { ...(draft.answer_data || {}), correct_options: newCorrect },
      }));

    return (
      <McqOptions
        options={options}
        setOptions={(newOptions) => writeBack(newOptions, correctIndices)}
        isMultiSelect={isMultiSelect}
        correctIndex={correctIndices[0] ?? null}
        setCorrectIndex={(i) => writeBack(options, [i])}
        correctIndices={correctIndices}
        setCorrectIndices={(list) => writeBack(options, list)}
      />
    );
  };

  const renderOptionsEditor = (entry, index) =>
    renderOptionsEditorFor(entry.question || {}, (updater) => updateQuestion(index, updater));

  /** Question/sub-question stem: read-only LaTeX-rendered view with a toggle into a rich-text editor. */
  const renderStemText = (text, editKey, onChange, displayHtml = null) => {
    const isEditing = editingTextKey === editKey;

    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: "auto",
            // Stems whose <table> came from the AI (rather than from table_layout) carry no
            // inline styling, and the sanitizer has already dropped border/cellpadding.
            "& table": { borderCollapse: "collapse", margin: "8px 0" },
            "& td, & th": { border: "1px solid #999", padding: "6px 10px", textAlign: "center" },
          }}
        >
          {isEditing ? (
            <AnswerOptionRichEditor
              value={text || ""}
              onChange={onChange}
              placeholder="Question text"
            />
          ) : (
            <HtmlWithLatex html={displayHtml ?? (text || "")} />
          )}
        </Box>
        <Tooltip title={isEditing ? "Done editing" : "Edit question text"}>
          <IconButton
            size="small"
            onClick={() => setEditingTextKey(isEditing ? null : editKey)}
          >
            {isEditing ? <CheckIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  if (!jobId) {
    return <McqJobPicker />;
  }

  if (loading) {
    return (
      <Box sx={{ p: 6, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} size="small">
          Back
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          MCQ Conversion Review
        </Typography>
        {job?.import?.source_filename && (
          <Chip size="small" label={job.import.source_filename} />
        )}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon fontSize="small" />}
          onClick={() => setDeleteConfirmOpen(true)}
        >
          Delete conversion
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The correct option comes from the paper&rsquo;s existing mark scheme. Check the wrong
        options, then save the ones you approve as new question bank questions. The original
        questions are not changed.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <Chip color="success" size="small" label={`${stats.converted || 0} converted`} />
        <Chip color="warning" size="small" label={`${stats.skipped || 0} skipped`} />
        <Chip color="error" size="small" label={`${stats.failed || 0} failed`} />
        <Chip variant="outlined" size="small" label={`${approved.size} approved`} />
      </Stack>

      {job?.already_saved && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete & regenerate
            </Button>
          }
        >
          These questions have already been saved to the question bank
          {(job?.created_entities || []).length > 0 && (
            <>
              {" "}
              and used to create{" "}
              {(job.created_entities || [])
                .map((e) => `${ENTITY_LABELS[e.entity_type] || e.entity_type} “${e.name}”`)
                .join(", ")}
            </>
          )}
          . Delete them first if you want to convert this paper again instead of creating
          duplicates.
        </Alert>
      )}

      <JobDeleteGeneratedDialog
        nounSingular="generated MCQ"
        listLabel="MCQ Conversions"
        regenerateHint="Convert to MCQ"
        open={deleteConfirmOpen}
        job={job}
        busy={deleting}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteGenerated}
      />

      <ReviewFilterBar
        search={search}
        onSearchChange={setSearch}
        placeholder="Search question text, options, mark scheme…"
        filters={[
          {
            key: "status",
            label: "Status",
            value: statusFilter,
            onChange: setStatusFilter,
            minWidth: 140,
            options: [
              { value: "all", label: "All statuses" },
              { value: "converted", label: "Converted" },
              { value: "skipped", label: "Skipped" },
              { value: "failed", label: "Failed" },
            ],
          },
          {
            key: "type",
            label: "Type",
            value: typeFilter,
            onChange: setTypeFilter,
            minWidth: 140,
            options: [
              { value: "all", label: "All types" },
              ...availableTypes.map((t) => ({ value: t, label: t })),
            ],
          },
          {
            key: "subject",
            label: "Subject",
            value: subjectFilter,
            onChange: setSubjectFilter,
            minWidth: 170,
            options: [
              { value: "all", label: "All subjects" },
              ...availableSubjects.map((s) => ({ value: s.key, label: s.label })),
            ],
          },
          {
            key: "approval",
            label: "Approval",
            value: approvalFilter,
            onChange: setApprovalFilter,
            minWidth: 150,
            options: [
              { value: "all", label: "All" },
              { value: "approved", label: "Approved only" },
              { value: "unapproved", label: "Not approved" },
            ],
          },
        ]}
        visibleCount={visible.length}
        totalCount={questions.length}
        onApproveShown={() => setApprovalForVisible(true)}
        onClearShown={() => setApprovalForVisible(false)}
        bulkDisabled={visible.length === 0}
      />

      <Stack spacing={2}>
        {visible.length === 0 && (
          <Alert severity="info">No questions match the current filters.</Alert>
        )}
        {visible.map(({ entry, index }) => {
          const q = entry.question || {};
          const chip = statusChip(entry);
          const isApproved = approved.has(index);

          return (
            <Paper
              key={index}
              variant="outlined"
              sx={{
                p: 2,
                borderColor: isApproved ? "success.light" : "divider",
                borderWidth: isApproved ? 2 : 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Checkbox
                  checked={isApproved}
                  onChange={() => toggleApproved(index)}
                  size="small"
                />
                <Chip size="small" variant="outlined" label={`#${index + 1}`} />
                <Chip size="small" color={chip.color} label={chip.label} />
                {q.original_type && (
                  <Chip size="small" variant="outlined" label={`was: ${q.original_type}`} />
                )}
                {q.type && <Chip size="small" variant="outlined" label={q.type} />}

                <Box sx={{ flex: 1 }} />

                <Tooltip title="Regenerate this question with AI">
                  <span>
                    <Button
                      size="small"
                      startIcon={
                        regenIdx === index ? (
                          <CircularProgress size={14} />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )
                      }
                      onClick={() => handleRegenerate(index)}
                      disabled={regenIdx !== null}
                    >
                      Regenerate
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {entry.status !== "converted" && (
                <Alert severity={entry.status === "skipped" ? "warning" : "error"} sx={{ mb: 1.5 }}>
                  {SKIP_REASONS[entry.reason] || entry.reason || "Could not convert this question."}
                  {entry.sub_reasons && (
                    <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                      {Object.entries(entry.sub_reasons).map(([subId, reason]) => (
                        <li key={subId}>
                          <Typography variant="caption">
                            Sub-question #{subId}: {SKIP_REASONS[reason] || reason}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </Alert>
              )}

              {q.repaired && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={REPAIR_LABELS[q.repaired] || q.repaired}
                  sx={{ mb: 1.5 }}
                />
              )}

              {q.mark_scheme_answer && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  title={q.mark_scheme_answer}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    mb: 1,
                  }}
                >
                  Mark scheme answer: <strong>{q.mark_scheme_answer}</strong>
                </Typography>
              )}

              {q.original_type === "table" && q.repaired !== "rendered_option_images" && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Table blanks are filled in row-major order (left to right, top to bottom).
                </Typography>
              )}

              {renderStemText(
                q.question_text,
                `${index}`,
                (html) => updateQuestion(index, (draft) => ({ ...draft, question_text: html })),
                displayStemHtml(q)
              )}

              {collectImageUrls(q).length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
                  {collectImageUrls(q).map((imageUrl, imageIndex) => (
                    <Box
                      key={`${imageUrl}-${imageIndex}`}
                      component="img"
                      src={imageUrl}
                      alt=""
                      sx={{ maxWidth: "100%", maxHeight: 240, borderRadius: 1 }}
                    />
                  ))}
                </Box>
              )}

              {q.type === "grouped" ? (
                <Box sx={{ pl: 2, borderLeft: 3, borderColor: "primary.light" }}>
                  {(q.sub_questions || []).map((sq, sIdx) => {
                    const partNum =
                      typeof sq.sort_order === "number" ? sq.sort_order + 1 : sIdx + 1;
                    const subCount = (q.sub_questions || []).length;
                    return (
                      <Box key={sIdx} sx={{ mb: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ flex: 1 }}>
                            Part {partNum}
                          </Typography>
                          <Tooltip title="Move part up">
                            <span>
                              <IconButton
                                size="small"
                                disabled={sIdx === 0 || job?.already_saved}
                                onClick={() => moveSubQuestion(index, sIdx, -1)}
                              >
                                <ArrowUpwardIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move part down">
                            <span>
                              <IconButton
                                size="small"
                                disabled={sIdx >= subCount - 1 || job?.already_saved}
                                onClick={() => moveSubQuestion(index, sIdx, 1)}
                              >
                                <ArrowDownwardIcon fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        {sq.original_type === "table" && sq.repaired !== "rendered_option_images" && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                          >
                            Table blanks are filled in row-major order (left to right, top to
                            bottom).
                          </Typography>
                        )}
                        {renderStemText(
                          sq.question_text,
                          `${index}-${sIdx}`,
                          (html) =>
                            updateSubQuestion(index, sIdx, (draft) => ({
                              ...draft,
                              question_text: html,
                            })),
                          displayStemHtml(sq)
                        )}
                        {collectImageUrls(sq).length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
                            {collectImageUrls(sq).map((imageUrl, imageIndex) => (
                              <Box
                                key={`${imageUrl}-${imageIndex}`}
                                component="img"
                                src={imageUrl}
                                alt=""
                                sx={{ maxWidth: "100%", maxHeight: 200, borderRadius: 1 }}
                              />
                            ))}
                          </Box>
                        )}
                        <Box sx={{ mt: 1 }}>
                          {renderOptionsEditorFor(sq, (updater) =>
                            updateSubQuestion(index, sIdx, updater)
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                renderOptionsEditor(entry, index)
              )}
            </Paper>
          );
        })}
      </Stack>

      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          mt: 3,
          py: 2,
          bgcolor: "background.paper",
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={approved.size === 0 || job?.already_saved}
          onClick={openSaveDialog}
        >
          Save {approved.size} to Question Bank
        </Button>
      </Box>

      <SaveToBankDestinationDialog
        open={saveOpen}
        busy={saving}
        onClose={() => setSaveOpen(false)}
        onSubmit={handleSave}
        approvedCount={approved.size}
        destination={destination}
        onDestinationChange={changeDestination}
        form={entityForm}
        onFormChange={setEntityForm}
        categoryId={categoryId}
        onCategoryChange={setCategoryId}
        lookups={{
          formats: lookupFormats,
          schoolYears: lookupSchoolYears,
          topics: lookupTopics,
          loading: lookupsLoading,
        }}
      />
    </Box>
  );
};

export default McqReview;
